import type { CallState } from '../WebRTCCallService';
import { getAgentPhonePath } from './agentPhoneNavigation';
import {
  AGENT_PHONE_CHANNEL,
  AGENT_PHONE_DEFAULT_HEIGHT_PX,
  AGENT_PHONE_DEFAULT_WIDTH_PX,
  AGENT_PHONE_WINDOW_NAME,
  AGENT_PHONE_POPUP_BLOCKED_KEY,
  AGENT_PHONE_SESSION_PRIMED_KEY,
  PENDING_OUTBOUND_DIAL_KEY,
  PENDING_SCREEN_POP_KEY,
  type AgentPhoneCrmMessage,
  type AgentPhoneOutboundMessage,
  type AgentPhoneToCrmMessage,
  type PendingOutboundDial,
  type PendingScreenPop,
} from './types';

export { getAgentPhonePath };

const PHONE_PATH = '/app/agent-phone';

let phoneWindowRef: Window | null = null;
let channel: BroadcastChannel | null = null;

function getChannel(): BroadcastChannel | null {
  if (typeof BroadcastChannel === 'undefined') {
    return null;
  }
  if (!channel) {
    channel = new BroadcastChannel(AGENT_PHONE_CHANNEL);
  }
  return channel;
}

function post(message: AgentPhoneToCrmMessage | AgentPhoneCrmMessage): void {
  getChannel()?.postMessage(message);
  if (phoneWindowRef && !phoneWindowRef.closed) {
    phoneWindowRef.postMessage(message, window.location.origin);
  }
}

function randomRequestId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** True when running inside the dedicated agent phone window. */
export function isAgentPhoneWindow(): boolean {
  return typeof window !== 'undefined' && window.location.pathname === PHONE_PATH;
}

export function getAgentPhoneUrl(): string {
  return `${window.location.origin}${PHONE_PATH}`;
}

function buildAgentPhoneWindowFeatures(): string {
  const width = AGENT_PHONE_DEFAULT_WIDTH_PX;
  const height = AGENT_PHONE_DEFAULT_HEIGHT_PX;
  const left = Math.max(0, Math.round((window.screen.availWidth - width) / 2));
  const top = Math.max(0, Math.round((window.screen.availHeight - height) / 2));
  return [
    `width=${width}`,
    `height=${height}`,
    `left=${left}`,
    `top=${top}`,
    'menubar=no',
    'toolbar=no',
    'location=no',
    'status=no',
    'resizable=yes',
    'scrollbars=yes',
  ].join(',');
}

/** Queue an outbound dial for Agent Phone (survives same-tab navigation). */
export function stashPendingOutboundDial(message: AgentPhoneOutboundMessage): void {
  const pending: PendingOutboundDial = { message, at: Date.now() };
  localStorage.setItem(PENDING_OUTBOUND_DIAL_KEY, JSON.stringify(pending));
}

/** Read queued dial without removing (safe for React Strict Mode double-mount). */
export function peekPendingOutboundDial(maxAgeMs = 120_000): AgentPhoneOutboundMessage | null {
  try {
    const raw = localStorage.getItem(PENDING_OUTBOUND_DIAL_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw) as PendingOutboundDial;
    if (!parsed?.message || !parsed?.at || Date.now() - parsed.at > maxAgeMs) {
      localStorage.removeItem(PENDING_OUTBOUND_DIAL_KEY);
      return null;
    }
    return parsed.message;
  } catch {
    return null;
  }
}

export function clearPendingOutboundDial(): void {
  try {
    localStorage.removeItem(PENDING_OUTBOUND_DIAL_KEY);
  } catch {
    /* ignore */
  }
}

export function isAgentPhoneWindowOpen(): boolean {
  return Boolean(phoneWindowRef && !phoneWindowRef.closed);
}

export function markAgentPhonePopupBlocked(blocked: boolean): void {
  try {
    if (blocked) {
      sessionStorage.setItem(AGENT_PHONE_POPUP_BLOCKED_KEY, '1');
    } else {
      sessionStorage.removeItem(AGENT_PHONE_POPUP_BLOCKED_KEY);
    }
  } catch {
    /* ignore */
  }
}

export function wasAgentPhonePopupBlocked(): boolean {
  try {
    return sessionStorage.getItem(AGENT_PHONE_POPUP_BLOCKED_KEY) === '1';
  } catch {
    return false;
  }
}

export function isAgentPhoneSessionPrimed(): boolean {
  try {
    return sessionStorage.getItem(AGENT_PHONE_SESSION_PRIMED_KEY) === '1';
  } catch {
    return false;
  }
}

function markAgentPhoneSessionPrimed(): void {
  try {
    sessionStorage.setItem(AGENT_PHONE_SESSION_PRIMED_KEY, '1');
  } catch {
    /* ignore */
  }
}

/**
 * Request pop-up permission during a user gesture (login) without opening Agent Phone.
 * Opens a tiny named window and closes it immediately so later opens are allowed.
 */
export function requestAgentPhonePopupPermission(): { allowed: boolean; blocked: boolean } {
  if (isAgentPhoneWindow()) {
    markAgentPhonePopupBlocked(false);
    markAgentPhoneSessionPrimed();
    return { allowed: true, blocked: false };
  }

  if (phoneWindowRef && !phoneWindowRef.closed) {
    markAgentPhonePopupBlocked(false);
    markAgentPhoneSessionPrimed();
    return { allowed: true, blocked: false };
  }

  const probe = window.open(
    'about:blank',
    AGENT_PHONE_WINDOW_NAME,
    [
      'width=1',
      'height=1',
      'left=-2000',
      'top=-2000',
      'menubar=no',
      'toolbar=no',
      'location=no',
      'status=no',
    ].join(','),
  );

  if (!probe) {
    markAgentPhonePopupBlocked(true);
    return { allowed: false, blocked: true };
  }

  try {
    probe.close();
  } catch {
    /* ignore */
  }
  phoneWindowRef = null;

  markAgentPhonePopupBlocked(false);
  markAgentPhoneSessionPrimed();
  return { allowed: true, blocked: false };
}

/** Open Agent Phone during a user gesture so pop-up blockers do not interfere. */
export function primeAgentPhonePopup(): { opened: boolean; blocked: boolean } {
  if (isAgentPhoneWindow()) {
    markAgentPhonePopupBlocked(false);
    markAgentPhoneSessionPrimed();
    return { opened: true, blocked: false };
  }

  const win = openAgentPhoneWindow();
  if (!win) {
    markAgentPhonePopupBlocked(true);
    return { opened: false, blocked: true };
  }

  markAgentPhonePopupBlocked(false);
  markAgentPhoneSessionPrimed();
  return { opened: true, blocked: false };
}

export function closeAgentPhoneWindow(): void {
  try {
    if (phoneWindowRef && !phoneWindowRef.closed) {
      phoneWindowRef.close();
    }
  } catch {
    /* ignore */
  }
  phoneWindowRef = null;
}

/** Open or focus the dedicated Agent Phone pop-out window (CRM stays on the current page). */
export function openAgentPhoneWindow(): Window | null {
  if (isAgentPhoneWindow()) {
    return window;
  }

  if (phoneWindowRef && !phoneWindowRef.closed) {
    phoneWindowRef.focus();
    return phoneWindowRef;
  }

  phoneWindowRef = window.open(
    getAgentPhoneUrl(),
    AGENT_PHONE_WINDOW_NAME,
    buildAgentPhoneWindowFeatures(),
  );
  if (phoneWindowRef) {
    markAgentPhonePopupBlocked(false);
    markAgentPhoneSessionPrimed();
  }
  return phoneWindowRef;
}

/** @deprecated Use openAgentPhoneWindow */
export const openAgentPhoneTab = openAgentPhoneWindow;

/** Open Agent Phone in a separate window without leaving the CRM screen. */
export function navigateToAgentPhoneScreen(): void {
  if (isAgentPhoneWindow()) {
    return;
  }
  openAgentPhoneWindow();
}

export function publishCallState(state: CallState): void {
  if (!isAgentPhoneWindow()) {
    return;
  }
  post({ type: 'CALL_STATE', state });
}

/**
 * CRM → pop-out: ask the phone window to re-broadcast its current call state.
 * The phone window responds with a STATE_SYNC message.
 */
export function requestCallStateSync(): void {
  if (isAgentPhoneWindow()) {
    return;
  }
  getChannel()?.postMessage({ type: 'STATE_REQUEST' });
  if (phoneWindowRef && !phoneWindowRef.closed) {
    phoneWindowRef.postMessage({ type: 'STATE_REQUEST' }, window.location.origin);
  }
}

/** Used by the phone pop-out to send messages back to the CRM (avoids creating throwaway channels). */
export function postFromPhone(message: AgentPhoneToCrmMessage): void {
  if (!isAgentPhoneWindow()) {
    return;
  }
  post(message as unknown as AgentPhoneToCrmMessage | AgentPhoneCrmMessage);
}

export function publishScreenPop(payload: {
  clientId?: string | null;
  clientName?: string | null;
  clientPhone?: string | null;
  threadId?: string | null;
  callSid?: string | null;
}): void {
  if (!isAgentPhoneWindow()) {
    return;
  }
  post({ type: 'SCREEN_POP', ...payload });
  try {
    const pending: PendingScreenPop = {
      clientId: payload.clientId,
      clientName: payload.clientName,
      clientPhone: payload.clientPhone,
      threadId: payload.threadId,
      at: Date.now(),
    };
    sessionStorage.setItem(PENDING_SCREEN_POP_KEY, JSON.stringify(pending));
  } catch {
    /* ignore quota errors */
  }
}

export function publishPhoneReady(): void {
  post({ type: 'PHONE_READY' });
}

export function consumePendingScreenPop(maxAgeMs = 120_000): PendingScreenPop | null {
  try {
    const raw = sessionStorage.getItem(PENDING_SCREEN_POP_KEY);
    if (!raw) {
      return null;
    }
    sessionStorage.removeItem(PENDING_SCREEN_POP_KEY);
    const parsed = JSON.parse(raw) as PendingScreenPop;
    if (!parsed?.at || Date.now() - parsed.at > maxAgeMs) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function subscribeAgentPhoneMessages(
  handler: (message: AgentPhoneToCrmMessage | AgentPhoneCrmMessage) => void,
): () => void {
  const bc = getChannel();
  const onBc = (event: MessageEvent) => {
    handler(event.data);
  };
  bc?.addEventListener('message', onBc);

  const onWindow = (event: MessageEvent) => {
    if (event.origin !== window.location.origin) {
      return;
    }
    handler(event.data);
  };
  window.addEventListener('message', onWindow);

  return () => {
    bc?.removeEventListener('message', onBc);
    window.removeEventListener('message', onWindow);
  };
}

async function sendDialRequest(message: AgentPhoneOutboundMessage): Promise<{ ok: boolean; error?: string }> {
  try {
    stashPendingOutboundDial(message);
  } catch {
    return { ok: false, error: 'Could not queue outbound call. Try again.' };
  }

  const phoneWin = openAgentPhoneWindow();
  if (!phoneWin) {
    return {
      ok: false,
      error: 'Could not open Agent Phone. Allow pop-ups for this site and try again.',
    };
  }

  // Phone tab may already be listening; localStorage covers the first-load race.
  post(message);
  return { ok: true };
}


export async function requestPstnDial(options: {
  phoneNumber: string;
  clientName?: string;
  clientId?: string;
  agentId?: string;
  channel?: CallState['channel'];
}): Promise<{ ok: boolean; error?: string }> {
  if (isAgentPhoneWindow()) {
    return { ok: false, error: 'Use the call controls in this window.' };
  }

  return sendDialRequest({
    type: 'DIAL_PSTN',
    requestId: randomRequestId(),
    ...options,
  });
}

export async function requestAppDial(options: {
  clientId: string;
  clientName: string;
  agentId: string;
  agentDisplayName: string;
}): Promise<{ ok: boolean; error?: string }> {
  if (isAgentPhoneWindow()) {
    return { ok: false, error: 'Use the call controls in this window.' };
  }

  return sendDialRequest({
    type: 'DIAL_APP',
    requestId: randomRequestId(),
    ...options,
  });
}

export function getCachedCallStateFromStorage(): CallState | null {
  try {
    const raw = sessionStorage.getItem('cwy-agent-phone-call-state');
    if (!raw) {
      return null;
    }
    return JSON.parse(raw) as CallState;
  } catch {
    return null;
  }
}

export function cacheCallStateForCrm(state: CallState): void {
  if (isAgentPhoneWindow()) {
    return;
  }
  try {
    sessionStorage.setItem('cwy-agent-phone-call-state', JSON.stringify(state));
  } catch {
    /* ignore */
  }
}
